/**
 * Admin Notifications Management Page
 * Send and manage push notifications
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API } from "@/context/AppContext";
import { 
  Bell, Send, Users, Calendar, TrendingUp, 
  Loader2, ArrowLeft, CheckCircle, AlertCircle,
  MessageSquare, Clock
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const AdminNotificationsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [targetType, setTargetType] = useState("all");
  const [customVariables, setCustomVariables] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/notifications/templates`, { withCredentials: true }),
        axios.get(`${API}/admin/notifications/stats`, { withCredentials: true })
      ]);
      setTemplates(templatesRes.data.templates || []);
      setCategories(templatesRes.data.categories || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const sendNotification = async () => {
    if (!selectedTemplate) {
      toast.error("Bitte wählen Sie eine Vorlage");
      return;
    }
    
    try {
      setSending(true);
      const res = await axios.post(`${API}/admin/notifications/send`, null, {
        params: {
          template_type: selectedTemplate,
          target_type: targetType,
          category: "promotions",
          priority: "normal"
        },
        withCredentials: true
      });
      
      toast.success(`${res.data.sent} Benachrichtigungen gesendet`);
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} fehlgeschlagen`);
      }
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Senden");
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const sendWeeklyDigest = async () => {
    try {
      setSending(true);
      const res = await axios.post(`${API}/admin/notifications/send-weekly-digest`, {}, { withCredentials: true });
      toast.success(`Wöchentliche Zusammenfassung: ${res.data.sent} gesendet`);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Senden der Zusammenfassung");
    } finally {
      setSending(false);
    }
  };

  const sendWeeklyTip = async () => {
    try {
      setSending(true);
      const res = await axios.post(`${API}/admin/notifications/send-tip`, {}, { withCredentials: true });
      toast.success(`Wöchentlicher Tipp: ${res.data.sent} gesendet`);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Senden des Tipps");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-notifications-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin')} className="p-2" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Benachrichtigungen</h1>
          <p className="text-gray-500 mt-1">Push-Benachrichtigungen verwalten und senden</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Gesendet (7 Tage)</p>
                  <p className="text-2xl font-bold">{stats.total_sent_7d}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Erfolgreich</p>
                  <p className="text-2xl font-bold">{stats.successful_7d}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Erfolgsrate</p>
                  <p className="text-2xl font-bold">{stats.success_rate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Bell className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Kategorien</p>
                  <p className="text-2xl font-bold">{Object.keys(stats.by_category || {}).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="send" className="w-full">
        <TabsList>
          <TabsTrigger value="send">Benachrichtigung senden</TabsTrigger>
          <TabsTrigger value="scheduled">Geplante Aktionen</TabsTrigger>
          <TabsTrigger value="templates">Vorlagen</TabsTrigger>
        </TabsList>

        <TabsContent value="send" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Benachrichtigung senden
              </CardTitle>
              <CardDescription>
                Senden Sie Push-Benachrichtigungen an Benutzer
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Vorlage</label>
                  <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                    <SelectTrigger data-testid="template-select">
                      <SelectValue placeholder="Vorlage wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(t => (
                        <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Zielgruppe</label>
                  <Select value={targetType} onValueChange={setTargetType}>
                    <SelectTrigger data-testid="target-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Benutzer</SelectItem>
                      <SelectItem value="clients">Nur Kunden</SelectItem>
                      <SelectItem value="providers">Nur Anbieter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <Button 
                onClick={sendNotification} 
                disabled={sending || !selectedTemplate}
                className="w-full md:w-auto"
                data-testid="send-notification-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Benachrichtigung senden
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="scheduled" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Geplante Aktionen (Manuell)
              </CardTitle>
              <CardDescription>
                Führen Sie geplante Benachrichtigungen manuell aus
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-blue-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-blue-600" />
                          Wöchentliche Zusammenfassung
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          Top-Fachleute, neue Anbieter, Statistiken
                        </p>
                        <Badge variant="outline" className="mt-2">
                          <Clock className="w-3 h-3 mr-1" />
                          Montag 08:30
                        </Badge>
                      </div>
                      <Button 
                        onClick={sendWeeklyDigest} 
                        disabled={sending}
                        size="sm"
                        data-testid="send-digest-btn"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Jetzt senden"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold flex items-center gap-2">
                          <Bell className="w-4 h-4 text-green-600" />
                          Tipp der Woche
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          Hilfreiche Tipps zur App-Nutzung
                        </p>
                        <Badge variant="outline" className="mt-2">
                          <Clock className="w-3 h-3 mr-1" />
                          Sonntag 10:00
                        </Badge>
                      </div>
                      <Button 
                        onClick={sendWeeklyTip} 
                        disabled={sending}
                        size="sm"
                        data-testid="send-tip-btn"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Jetzt senden"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-yellow-800">
                  <strong>Hinweis:</strong> Diese Aktionen werden derzeit manuell ausgelöst. 
                  Für automatische Planung kann später ein Scheduler implementiert werden.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Verfügbare Vorlagen</CardTitle>
              <CardDescription>
                Vorlagen für verschiedene Benachrichtigungstypen (4 Sprachen: DE, EN, FR, IT)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => (
                  <Card key={template} className="border hover:border-blue-300 transition-colors">
                    <CardContent className="p-4">
                      <h4 className="font-semibold capitalize">{template.replace(/_/g, ' ')}</h4>
                      <div className="flex gap-1 mt-2">
                        {['de', 'en', 'fr', 'it'].map(lang => (
                          <Badge key={lang} variant="outline" className="text-xs">
                            {lang.toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Benachrichtigungskategorien</CardTitle>
              <CardDescription>
                Benutzer können diese Kategorien in ihren Einstellungen aktivieren/deaktivieren
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {categories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{cat.name_de}</p>
                      <p className="text-sm text-gray-500">{cat.name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={cat.default ? "default" : "secondary"}>
                        {cat.default ? "Standard AN" : "Standard AUS"}
                      </Badge>
                      <Badge variant="outline">{cat.frequency}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminNotificationsPage;
